import { IntermediatePlate } from "features/WorklistTools/shared/components/IntermediatePlate";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import { useCtrlDown } from "features/WorklistTools/shared/hooks/hooks";
import {
  PlatePosition,
  ReactSelectableFast,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { SelectableGroup } from "react-selectable-fast";
import styled from "styled-components";
import { ExternalThawToolActions, IntermediatePlateInfoState } from "../state";
import { ExternalThawInternalAction } from "../state/actions";
import { useExternalThawStateProvider } from "../state/StateProvider";
import { IntWellVol } from "./components/IntWellVol";

const StyledSelectableGroup = styled(SelectableGroup)`
  height: 320px;
  width: 100%;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
`;

const StyledIntPlatesContainer = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

export const SelectableIntPlatesContainer = (intPlateTypeInfo: any) => {
  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();

  const dispatch = useAppReduxDispatch();
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.sourcePlateInfo
  );
  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.worklistValues
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.intPlateInfo
  );
  const handleIntermediateWellSelection = (
    selection: ReactSelectableFast[]
  ) => {
    const intSelection = selection.map((well) => ({
      plateIndex: well.props.plateIndex,
      plateWellId: well.props.plateWellId,
      isSelectable: well.props.wellColor === "" ? false : true,
    }));
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_SELECTED_INTERMEDIATE_WELLS,
      payload: {
        selectedIntermediateWells: intSelection,
      },
    });
  };

  const handleIntPlateInfoChanged = (
    index: number,
    key: string,
    value: any
  ) => {
    dispatch(
      ExternalThawToolActions.UPDATE_INT_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleWellHover = (wellId: string) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_HOVER_WELL_ID,
      payload: {
        hoverWellId: wellId,
      },
    });
  };

  const ctrlDown = useCtrlDown();

  return (
    <StyledIntPlatesContainer>
      {intPlateInfo.map((item: IntermediatePlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Intermediate}
            index={index}
            label={`Intermediate Plate Barcode ${index + 1}`}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleIntPlateInfoChanged(index!, field, value)
            }
            disabled={item.plateBarcode === ""}
          />
          <div>
            <StyledSelectableGroup
              onSelectionFinish={handleIntermediateWellSelection}
              resetOnStart={
                ctrlDown.worklistToolInternalState.setCtrlDown ? false : true
              }
              allowCtrlClick={true}
            >
              <IntermediatePlate
                worklistTool={WorklistTools.ExternalThaw}
                index={index}
                plateRows={item.rows}
                plateCols={item.cols}
                wellMapping={worklistValues.harvestWells}
                sourcePlateInfo={sourcePlateInfo}
                sourcePlatePosition={
                  index === 0
                    ? PlatePosition.Source
                    : PlatePosition.Intermediate
                }
                handleWellHover={handleWellHover}
              />
            </StyledSelectableGroup>
          </div>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Intermediate}
              isDisabled={
                externalThawInternalState.methodSettings!.selectedSystem === 0
              }
              acceptedPlateTypes={intPlateTypeInfo.intPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handleIntPlateInfoChanged(
                  index,
                  "labwareTypeCode",
                  plateTypeInfo
                )
              }
            />
            <div style={{ display: "flex", width: "100%" }}>
              <NumberField
                style={{ width: "50%", marginTop: "10px", marginRight: "5px" }}
                id="operating-vol"
                label="Operating Vol (µL)"
                value={item.operatingVol}
                onChange={(value) =>
                  handleIntPlateInfoChanged(
                    index,
                    "operatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
              <IntWellVol
                intWellVol={item.intWellVol}
                operatingVol={item.operatingVol}
              />
            </div>
          </StyledPlateSettingsContainer>
        </div>
      ))}
    </StyledIntPlatesContainer>
  );
};
